import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { useUser } from '/imports/hooks';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { PageFooter } from '/imports/ui/PageFooter';
import { Form } from '/imports/ui/Form';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { Modal } from '/imports/ui/Modal';
import { toastOnError } from '/imports/ui/utils';
import { formatBytes, readFileAsText } from '/imports/utils';
import { maxTextImportBytes } from '/imports/api/files';
import {
  createPowerhubCredential, getPowerhubCredentialsForOrg, importPowerhubSitesData,
  PowerhubCredential, pullPowerhubSites, updatePowerhubCredential
} from '/imports/api/powerhubMeters';

const PowerhubMeterPageStyled = styled(Page)`
  .result {
    margin: 2rem 0;
    .buttons {
      display: flex;
      gap: 1rem;
      margin-top: 1rem;
    }
  }
  table {
    td {
      padding: 1rem;
      &:first-child {
        padding-left: 0;
      }
    }
  }
  textarea {
    height: 14rem;
  }
  #import-file-input {
    display: none;
  }
`;

const UpdateCredentialsModalStyled = styled(Modal)`
  .modal-window {
    min-width: 28rem;
  }
  .buttons {
    margin-top: 1rem;
  }
`;

/** Super-user page to manage Powerhub credentials and import/pull sites. */
export const PowerhubMeterPage = () => {
  const user = useUser();
  if (!user?.isSuperUser) return <NotPermittedPage />;
  const orgId = user.orgId;
  const [clientId, setClientId] = useState<string>();
  const [clientSecret, setClientSecret] = useState<string>();
  const [credentials, setPowerhubCredentials] = useState<PowerhubCredential[]>([]);
  const [editCredential, setEditCredential] = useState<PowerhubCredential>();
  const [editClientId, setEditClientId] = useState<string>();
  const [editClientSecret, setEditClientSecret] = useState<string>();
  useEffect(() => {
    (async () => {
      if (!orgId) return;
      setPowerhubCredentials(await getPowerhubCredentialsForOrg.call(orgId));
    })();
  }, [orgId]);
  const pullSites = toastOnError(async (tokenId: string) => {
    const { numSystems, numCreated } = await pullPowerhubSites.call(tokenId);
    toast.success(`${numSystems} ${strings.SystemsFound()} | ${numCreated} ${strings.Created()}`);
  });
  const save = toastOnError(async () => {
    if (!clientId || !clientSecret || !orgId) return;
    await createPowerhubCredential.call({ clientId, clientSecret });
    setPowerhubCredentials(await getPowerhubCredentialsForOrg.call(orgId));
  });
  const openUpdateCredentials = (credential: PowerhubCredential) => {
    setEditCredential(credential);
    setEditClientId(credential.clientId);
    setEditClientSecret(undefined);  // the stored secret is encrypted — always re-entered, never shown
  };
  const saveCredentials = toastOnError(async () => {
    if (!editCredential || !editClientId || !editClientSecret || !orgId) return;
    await updatePowerhubCredential.call({ _id: editCredential._id, clientId: editClientId, clientSecret: editClientSecret });
    toast.success(strings.Updated());
    setEditCredential(undefined);
    setPowerhubCredentials(await getPowerhubCredentialsForOrg.call(orgId));
  });
  const importSites = toastOnError(async (file?: File) => {
    if (!file) return;
    // Guarded here rather than in the upload methods: this file is never stored, it rides to the server
    // whole as one method argument, and an oversized one closes the websocket instead of erroring.
    if (file.size > maxTextImportBytes) throw new Error(strings.FileTooLarge(file.name, formatBytes(file.size), formatBytes(maxTextImportBytes)));
    const content = await readFileAsText(file);
    const input = document.querySelector<HTMLInputElement>('#import-file-input');
    if (input) input.value = '';
    if (content) await importPowerhubSitesData.call(content);
    toast.success(strings.Done());
  });
  return (
    <PowerhubMeterPageStyled className="page">
      <PageHeading
        left={<h1>Powerhub</h1>}
        right={(
          <>
            <Button tip={strings.PowerhubImportTip()} onClick={() => document.getElementById('import-file-input')?.click()}>{strings.Import()}</Button>
            <input type="file" id="import-file-input" onChange={(e) => importSites(e.target.files?.[0])} />
          </>
        )}
      />
      <PageBody>
        <Form>
          <div className="field-group">
            <FormField field="clientId">
              <label>{strings.ClientId()}</label>
              <Input value={clientId}
                onChange={(clientId) => setClientId(clientId)} />
            </FormField>
            <FormField field="clientSecret">
              <label>{strings.ClientSecret()}</label>
              <Input value={clientSecret} type="password"
                onChange={(clientSecret) => setClientSecret(clientSecret)} />
            </FormField>
          </div>
          <div><Button onClick={() => save()}>{strings.Save()}</Button></div>
        </Form>
        {
          credentials.length ?
            <div>
              <h2>{strings.Accounts()}</h2>
              <table>
                <tbody>
                  {
                    credentials.map((t) => (
                      <tr key={t._id}>
                        <td>{t.clientId}</td>
                        <td><Button onClick={() => pullSites(t._id)}>{strings.PullSystems()}</Button></td>
                        <td><Button onClick={() => openUpdateCredentials(t)}>{strings.UpdateCredentials()}</Button></td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
            :
            null
        }
        {
          editCredential &&
          <UpdateCredentialsModalStyled
            title={strings.UpdateCredentials()}
            onClose={() => setEditCredential(undefined)}
          >
            <Form onSubmit={saveCredentials}>
              <FormField field="clientId">
                <label>{strings.ClientId()}</label>
                <Input value={editClientId} onChange={(v) => setEditClientId(v)} />
              </FormField>
              <FormField field="clientSecret">
                <label>{strings.ClientSecret()}</label>
                <Input value={editClientSecret} type="password" onChange={(v) => setEditClientSecret(v)} />
              </FormField>
              <div className="buttons">
                <Button type="submit" disabled={!editClientId || !editClientSecret}>{strings.Save()}</Button>
              </div>
            </Form>
          </UpdateCredentialsModalStyled>
        }
      </PageBody>
      <PageFooter
      />
    </PowerhubMeterPageStyled>
  );
};
